/**
 * Join (Figma 10 · Join 01–12): the page an invitation email opens. It already
 * knows who the person is, who invited them and to what, so they set a password
 * once (or use Google, or sign in) and are inside. Every ended, withdrawn, full
 * or already-used invitation has its own page.
 */
import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { CircleCheck, CircleX, Hourglass, Link2Off, Undo2, UserRound, UsersRound, WifiOff } from "lucide-react";
import { useAuthStore } from "@/stores/authStore";
import { useWorkspaceStore } from "@/stores/workspaceStore";
import { axiosInstance, signOutOnServer } from "@/lib/api";
import { PublicShell, Still } from "@/components/gettingIn/PublicShell";
import {
  Agreement,
  Column,
  GiButton,
  GiField,
  GoogleButton,
  Head,
  Note,
  Notice,
  OrDivider,
  QuietAction,
  StatusHead,
  Ways,
} from "@/components/gettingIn/parts";
import { SignInNoticeView } from "@/components/gettingIn/SignInNotice";
import { useSignIn } from "@/components/gettingIn/useSignIn";
import { usePasswordRule } from "@/components/gettingIn/usePasswordRule";
import { googleAddress, useGoogleToken } from "@/components/gettingIn/useGoogleToken";
import { readAuthFailure, type Session } from "@/components/gettingIn/authApi";
import { formatDay } from "@/app/routes/dashboard/components/settings/parts/settingsFormat";
import { firstName, invitationCalls, sendDecline, type Invitation } from "./invitation";

/* ------------------------------------------------------------ the panel */

function InvitationLines({ invitation }: { invitation: Invitation }) {
  const isOwnerStart = invitation.role === "Owner";
  return (
    <div className="flex flex-col gap-3.5">
      <p className="gi-invite-line">{isOwnerStart ? "You’re invited to start" : "You’re invited to join"}</p>
      <p className="gi-invite-line">
        <span className="gi-highlight">{invitation.workspace.name}</span>
      </p>
      <p className="gi-invite-line">on Creo.</p>
      <p className="text-[14px] leading-[19px] text-co-muted">
        Invited by {invitation.inviter.name || invitation.inviter.email || "your team"}
      </p>
    </div>
  );
}

function InvitationPanel({ invitation }: { invitation: Invitation }) {
  return (
    <div className="gi-invite-panel">
      <div className="flex min-h-0 flex-1 flex-col gap-8">
        <div className="gi-invite-still">
          <Still name="arches" />
        </div>
        <InvitationLines invitation={invitation} />
      </div>
      <p className="pt-8 text-[12.5px] leading-[17px] text-co-muted">By Mohtawa, for brands across the UAE and KSA.</p>
    </div>
  );
}

function InvitationBand({ invitation }: { invitation: Invitation }) {
  return (
    <div className="gi-invite-band">
      <Still name="arches" wide />
      <div className="gi-invite-card">
        <InvitationLines invitation={invitation} />
      </div>
    </div>
  );
}

/* ------------------------------------------------------------ the page */

type Stage =
  | { kind: "form" }
  | { kind: "google"; accessToken: string; email: string }
  | { kind: "declined" };

export default function JoinPage() {
  const { inviteId = "" } = useParams();
  const query = useQuery({
    queryKey: ["invitation", inviteId],
    queryFn: () => invitationCalls.read(inviteId),
    retry: false,
    staleTime: Infinity,
  });

  if (query.isLoading) {
    return (
      <PublicShell still="arches" title="Your invitation" hideTop>
        <Column>
          <div className="flex w-full flex-col gap-4" aria-busy aria-label="Opening your invitation">
            <span className="h-[51px] w-3/4 animate-pulse rounded-card bg-co-hover" />
            <span className="h-6 w-1/2 animate-pulse rounded-card bg-co-hover" />
            <span className="mt-4 h-[74px] w-full animate-pulse rounded-card bg-co-hover" />
            <span className="h-[74px] w-full animate-pulse rounded-card bg-co-hover" />
            <span className="mt-4 h-12 w-full animate-pulse rounded-pill bg-co-hover" />
          </div>
        </Column>
      </PublicShell>
    );
  }

  if (query.isError || !query.data) {
    const offline = readAuthFailure(query.error).kind === "offline";
    return (
      <PublicShell still="arches" title="Your invitation">
        <Column>
          {offline ? (
            <StatusHead icon={WifiOff} title="Creo cannot be reached">
              Your connection may be down, or we are. Nothing is lost; open the link again in a moment.
            </StatusHead>
          ) : (
            <StatusHead icon={Link2Off} title="This link does not open an invitation">
              It may have been copied only in part. Open it from the email again.
            </StatusHead>
          )}
          <Ways>
            {offline ? <GiButton onClick={() => void query.refetch()}>Try again</GiButton> : null}
            <GiButton variant={offline ? "secondary" : "primary"} onClick={() => (window.location.href = "/login")}>
              Sign in
            </GiButton>
          </Ways>
        </Column>
      </PublicShell>
    );
  }

  return <JoinWith invitation={query.data} />;
}

function JoinWith({ invitation }: { invitation: Invitation }) {
  const navigate = useNavigate();
  const token = useAuthStore((s) => s.token);
  const [stage, setStage] = useState<Stage>({ kind: "form" });

  if (invitation.state !== "open") return <EndedInvitation invitation={invitation} />;
  if (stage.kind === "declined") {
    return <Declined invitation={invitation} onUndo={() => setStage({ kind: "form" })} />;
  }

  const panel = <InvitationPanel invitation={invitation} />;
  const band = <InvitationBand invitation={invitation} />;
  const decline = () => setStage({ kind: "declined" });

  return (
    <PublicShell panel={panel} band={band} hideTop title={`Join ${invitation.workspace.name}`}>
      <div key={stage.kind} className="gi-fade w-full max-w-[400px] max-lg:max-w-none">
        {token ? (
          <SignedIn invitation={invitation} onDecline={decline} onSignedOut={() => navigate(0)} />
        ) : stage.kind === "google" ? (
          <JoinWithGoogle
            invitation={invitation}
            accessToken={stage.accessToken}
            onBack={() => setStage({ kind: "form" })}
          />
        ) : invitation.hasAccount ? (
          <SignInToAccept invitation={invitation} onDecline={decline} onGoogle={(t, e) => setStage({ kind: "google", accessToken: t, email: e })} />
        ) : (
          <SetPassword invitation={invitation} onDecline={decline} onGoogle={(t, e) => setStage({ kind: "google", accessToken: t, email: e })} />
        )}
      </div>
    </PublicShell>
  );
}

/** After joining: the session, the workspace selected, and inside (team → Home, client → their landing). */
function useEnter() {
  const navigate = useNavigate();
  const login = useAuthStore((s) => s.login);
  const queryClient = useQueryClient();
  return (session: Session | null, organizationId?: string) => {
    if (session) login(session.token, session.refreshToken ?? "");
    if (organizationId) useWorkspaceStore.getState().setSelectedOrganizationId(organizationId);
    void queryClient.invalidateQueries({ queryKey: ["currentUser"] });
    navigate("/dashboard", { replace: true });
  };
}

/** Google, from either form: Google must hand back the invited address (Join 06). */
function useGoogleForJoin(invitation: Invitation, onMatch: (accessToken: string, email: string) => void) {
  const [notice, setNotice] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const start = useGoogleToken({
    onToken: async (accessToken) => {
      setPending(true);
      const email = await googleAddress(accessToken);
      setPending(false);
      if (!email) return setNotice("Google did not say which address it is. Try again, or set a password.");
      if (email.toLowerCase() !== invitation.email.toLowerCase()) {
        return setNotice(
          `Google signed you in as ${email}. This invitation is for ${invitation.email}. Use that Google account, or set a password.`,
        );
      }
      setNotice(null);
      onMatch(accessToken, email);
    },
    onFailure: () => setNotice("Google did not sign you in. Try again, or set a password."),
  });
  return { notice, pending, start: () => start() };
}

/* ------------------------------------------------------------ 01 / 02 / 07 */

function SetPassword({
  invitation,
  onDecline,
  onGoogle,
}: {
  invitation: Invitation;
  onDecline: () => void;
  onGoogle: (accessToken: string, email: string) => void;
}) {
  const enter = useEnter();
  const [name, setName] = useState("");
  const [nameError, setNameError] = useState<string | null>(null);
  const password = usePasswordRule();
  const [pending, setPending] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const google = useGoogleForJoin(invitation, onGoogle);

  const join = async () => {
    const nameOk = name.trim().length >= 2;
    setNameError(nameOk ? null : "Add your name.");
    if (!password.check() || !nameOk) return;
    setPending(true);
    setFailure(null);
    try {
      enter(await invitationCalls.join(invitation.id, name.trim(), password.value));
    } catch (error) {
      const f = readAuthFailure(error);
      setFailure(
        f.kind === "offline"
          ? "Creo cannot be reached. Check your connection; what you typed stays."
          : f.kind === "coded" && f.code === "INVITATION_NOT_OPEN"
            ? "This invitation can no longer be accepted. Reload the page to see why."
            : "Joining did not work. Try again in a moment.",
      );
    } finally {
      setPending(false);
    }
  };

  return (
    <form
      className="flex w-full flex-col gap-6"
      noValidate
      onSubmit={(e) => {
        e.preventDefault();
        void join();
      }}
    >
      <Head title="Set your password" sub="Add your name and a password." align="start" />
      {failure ? <Notice tone="attention">{failure}</Notice> : null}
      {google.notice ? <Notice tone="info" icon={UserRound}>{google.notice}</Notice> : null}
      <div className="flex w-full flex-col gap-4">
        <GiField label="Email" type="email" value={invitation.email} locked hint="The invitation is for this address." autoComplete="username" />
        <GiField
          label="Your name"
          autoComplete="name"
          placeholder="First and last name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          error={nameError}
          readOnly={pending}
        />
        <GiField
          label="Password"
          secret
          autoComplete="new-password"
          placeholder="Choose a password"
          hint={password.hint}
          error={password.error}
          readOnly={pending}
          {...password.field}
        />
      </div>
      <Ways>
        <GiButton type="submit" loading={pending}>
          Join {invitation.workspace.name}
        </GiButton>
        <OrDivider />
        <GoogleButton type="button" onClick={google.start} loading={google.pending} disabled={pending} />
      </Ways>
      <div className="flex flex-col items-start gap-1">
        <Agreement />
        <QuietAction onClick={onDecline}>Not for me</QuietAction>
      </div>
    </form>
  );
}

/* ------------------------------------------------------------ 04 */

function SignInToAccept({
  invitation,
  onDecline,
  onGoogle,
}: {
  invitation: Invitation;
  onDecline: () => void;
  onGoogle: (accessToken: string, email: string) => void;
}) {
  const enter = useEnter();
  const login = useAuthStore((s) => s.login);
  const [password, setPassword] = useState("");
  const [acceptFailure, setAcceptFailure] = useState<string | null>(null);
  const google = useGoogleForJoin(invitation, onGoogle);
  const passwordRef = useRef<HTMLInputElement>(null);

  const form = useSignIn({
    passwordRef,
    onSignedIn: async (session) => {
      login(session.token, session.refreshToken ?? "");
      try {
        const accepted = await invitationCalls.accept(invitation.id);
        enter(null, accepted.organizationId);
      } catch {
        // Signed in, but the accept failed: the invitation waits in Creo.
        setAcceptFailure("You are signed in, but joining did not finish. Your invitation is waiting inside Creo.");
        window.setTimeout(() => enter(null), 2500);
      }
    },
  });

  return (
    <form
      className="flex w-full flex-col gap-6"
      noValidate
      onSubmit={(e) => {
        e.preventDefault();
        if (password) void form.signIn(invitation.email, password);
      }}
    >
      <Head title="Sign in to accept" sub="This address already has an account." align="start" />
      <SignInNoticeView notice={form.notice} />
      {acceptFailure ? <Notice tone="attention">{acceptFailure}</Notice> : null}
      {google.notice ? <Notice tone="info" icon={UserRound}>{google.notice}</Notice> : null}
      <div className="flex w-full flex-col gap-4">
        <GiField label="Email" type="email" value={invitation.email} locked hint="The invitation is for this address." autoComplete="username" />
        <GiField
          ref={passwordRef}
          label="Password"
          secret
          autoComplete="current-password"
          placeholder="Your password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          readOnly={form.pending !== null}
        />
        <div className="flex w-full justify-end">
          <QuietAction to={`/forgot-password?email=${encodeURIComponent(invitation.email)}`}>Forgot password?</QuietAction>
        </div>
      </div>
      <Ways>
        <GiButton type="submit" loading={form.pending === "password"} disabled={form.waiting}>
          {form.notice?.kind === "offline" ? "Try again" : "Sign in and join"}
        </GiButton>
        <OrDivider />
        <GoogleButton type="button" onClick={google.start} loading={google.pending} />
      </Ways>
      <div className="flex flex-col items-start">
        <QuietAction onClick={onDecline}>Not for me</QuietAction>
      </div>
    </form>
  );
}

/* ------------------------------------------------------------ 06 */

function JoinWithGoogle({
  invitation,
  accessToken,
  onBack,
}: {
  invitation: Invitation;
  accessToken: string;
  onBack: () => void;
}) {
  const enter = useEnter();
  const [pending, setPending] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  const join = async () => {
    setPending(true);
    setFailure(null);
    try {
      enter(await invitationCalls.joinWithGoogle(invitation.id, accessToken));
    } catch (error) {
      const f = readAuthFailure(error);
      setFailure(
        f.kind === "offline"
          ? "Creo cannot be reached. Check your connection and try again."
          : f.kind === "coded" && typeof f.data.message === "string"
            ? f.data.message
            : "Joining did not work. Try again, or use a password instead.",
      );
    } finally {
      setPending(false);
    }
  };

  return (
    <div className="flex w-full flex-col gap-6">
      <Head title="One step left" sub="Google confirmed your address." align="start" />
      {failure ? <Notice tone="attention">{failure}</Notice> : null}
      <Notice tone="info" icon={CircleCheck}>
        {invitation.email} is confirmed. No password needed.
      </Notice>
      <GiButton onClick={() => void join()} loading={pending}>
        Join {invitation.workspace.name}
      </GiButton>
      <div className="flex flex-col items-start gap-1">
        <Agreement />
        <QuietAction onClick={onBack}>Use a password instead</QuietAction>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------ 05 / signed in */

function SignedIn({
  invitation,
  onDecline,
  onSignedOut,
}: {
  invitation: Invitation;
  onDecline: () => void;
  onSignedOut: () => void;
}) {
  const navigate = useNavigate();
  const enter = useEnter();
  const logout = useAuthStore((s) => s.logout);
  const me = useQuery({
    queryKey: ["currentUser", "join"],
    queryFn: () => axiosInstance.get<{ user: { email: string } }>("/auth/user-info").then((r) => r.data.user),
    retry: false,
  });
  const [pending, setPending] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  if (me.isLoading) return <Head title="Your invitation" align="start" />;
  const myEmail = me.data?.email ?? "";

  if (myEmail.toLowerCase() === invitation.email.toLowerCase()) {
    return (
      <div className="flex w-full flex-col gap-6">
        <Head title={`Join ${invitation.workspace.name}`} sub={`You are signed in as ${myEmail}.`} align="start" />
        {failure ? <Notice tone="attention">{failure}</Notice> : null}
        <GiButton
          loading={pending}
          onClick={async () => {
            setPending(true);
            try {
              const accepted = await invitationCalls.accept(invitation.id);
              enter(null, accepted.organizationId);
            } catch (error) {
              setFailure(
                readAuthFailure(error).kind === "offline"
                  ? "Creo cannot be reached. Check your connection and try again."
                  : "Joining did not work. Reload the page to see why.",
              );
            } finally {
              setPending(false);
            }
          }}
        >
          Join {invitation.workspace.name}
        </GiButton>
        <div className="flex flex-col items-start">
          <QuietAction onClick={onDecline}>Not for me</QuietAction>
        </div>
      </div>
    );
  }

  return (
    <div className="flex w-full flex-col gap-6">
      <Head title="Signed in as someone else" sub={`This invitation is for ${invitation.email}.`} align="start" />
      <Notice tone="info" icon={UserRound}>
        You are signed in as {myEmail || "another address"}. Sign out to accept as{" "}
        {firstName(null, invitation.email)}.
      </Notice>
      <Ways>
        <GiButton
          onClick={() => {
            signOutOnServer();
            logout();
            onSignedOut();
          }}
        >
          Sign out and continue
        </GiButton>
        <GiButton variant="secondary" onClick={() => navigate("/dashboard")}>
          Stay signed in
        </GiButton>
      </Ways>
      <div className="flex flex-col items-start">
        <QuietAction onClick={onDecline}>Not for me</QuietAction>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------ 12, M07 */

function Declined({ invitation, onUndo }: { invitation: Invitation; onUndo: () => void }) {
  const undone = useRef(false);
  const sent = useRef(false);
  const mounted = useRef(false);

  // The decline goes when the page goes: closing the tab, or leaving it here.
  // Leaving is checked a tick later, so a dev StrictMode remount isn't a leave.
  useEffect(() => {
    mounted.current = true;
    const send = () => {
      if (undone.current || sent.current) return;
      sent.current = true;
      sendDecline(invitation.id);
    };
    window.addEventListener("pagehide", send);
    return () => {
      mounted.current = false;
      window.removeEventListener("pagehide", send);
      window.setTimeout(() => {
        if (!mounted.current) send();
      }, 0);
    };
  }, [invitation.id]);

  const inviter = invitation.inviter.name || invitation.inviter.email || "Whoever invited you";
  return (
    <PublicShell still="arches" title="Invitation declined" hideTop>
      <div className="gi-fade w-full">
        <Column>
          <StatusHead icon={CircleX} title="Invitation declined">
            Change your mind later? Ask {inviter} to invite you again.
          </StatusHead>
          <Ways>
            <GiButton
              icon={<Undo2 aria-hidden className="size-4" />}
              onClick={() => {
                undone.current = true;
                onUndo();
              }}
            >
              Undo
            </GiButton>
          </Ways>
          <Note>Undo works until you close this page.</Note>
        </Column>
      </div>
    </PublicShell>
  );
}

/* ------------------------------------------------------------ 08 – 11 */

function EmailInviter({ invitation, primary = true }: { invitation: Invitation; primary?: boolean }) {
  if (!invitation.inviter.email) return null;
  const subject = encodeURIComponent(`My invitation to ${invitation.workspace.name}`);
  return (
    <GiButton
      variant={primary ? "primary" : "secondary"}
      onClick={() => (window.location.href = `mailto:${invitation.inviter.email}?subject=${subject}`)}
    >
      Email {firstName(invitation.inviter.name, invitation.inviter.email)}
    </GiButton>
  );
}

function EndedInvitation({ invitation }: { invitation: Invitation }) {
  const navigate = useNavigate();
  const inviter = invitation.inviter.name || invitation.inviter.email || "whoever invited you";
  const signIn = (
    <GiButton variant="secondary" onClick={() => navigate(`/login?email=${encodeURIComponent(invitation.email)}`)}>
      Sign in
    </GiButton>
  );

  const body = (() => {
    switch (invitation.state) {
      case "ended":
        return (
          <>
            <StatusHead icon={Hourglass} title="This invitation ended">
              It was for {invitation.email} and ended {formatDay(invitation.expiresAt)}. Ask {inviter} for a new one.
            </StatusHead>
            <Ways>
              <EmailInviter invitation={invitation} />
              {signIn}
            </Ways>
          </>
        );
      case "withdrawn":
        return (
          <>
            <StatusHead icon={Link2Off} title="This invitation was withdrawn">
              {invitation.revokedBy?.name || inviter} withdrew it on {formatDay(invitation.revokedAt)}. Ask them if
              that was a mistake.
            </StatusHead>
            <Ways>
              <EmailInviter invitation={invitation} />
              {signIn}
            </Ways>
          </>
        );
      case "no_seat":
        return (
          <>
            <StatusHead icon={UsersRound} title="No seat is free">
              All {invitation.maxSeats ?? ""} seats are taken. This link works again once a seat is free.
            </StatusHead>
            <Ways>
              <EmailInviter invitation={invitation} />
            </Ways>
            <Note>Your invitation stays open until {formatDay(invitation.expiresAt)}.</Note>
          </>
        );
      case "joined":
        return (
          <>
            <StatusHead icon={CircleCheck} title={`You are already in ${invitation.workspace.name}`}>
              {invitation.email} joined on {formatDay(invitation.acceptedAt)}. Sign in to carry on.
            </StatusHead>
            <Ways>
              <GiButton onClick={() => navigate(`/login?email=${encodeURIComponent(invitation.email)}`)}>Sign in</GiButton>
            </Ways>
          </>
        );
      default:
        return (
          <>
            <StatusHead icon={CircleX} title="Invitation declined">
              This invitation was declined. Ask {inviter} to invite you again if that changes.
            </StatusHead>
            <Ways>
              <EmailInviter invitation={invitation} />
              {signIn}
            </Ways>
          </>
        );
    }
  })();

  return (
    <PublicShell still="arches" title={`${invitation.workspace.name} invitation`} hideTop>
      <Column>{body}</Column>
    </PublicShell>
  );
}
